import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import AdminLayout from "../../../layouts/AdminLayout";
import {
  FormField,
  TextInput,
  SelectDropdown,
  Button,
} from "../../../components/ui";
import { createSociety } from "../../../services/societyService";
import masterService from "../../../services/masterEntitiesService";
import { ArrowLeft, Building2 } from "lucide-react";
import { toast } from "react-toastify";

const DEFAULT_PROPERTY_TYPES = [
  { value: "Residential", label: "Residential Society (RESIDENTIAL)" },
  { value: "Commercial", label: "Commercial Complex (COMMERCIAL)" },
  { value: "Industrial", label: "Industrial Facility / Plant (INDUSTRIAL)" },
  { value: "Gated Villa Community", label: "Gated Villa Community (VILLA_COMMUNITY)" },
  { value: "Co-Working Space", label: "Co-Working Space (CO_WORKING)" },
  { value: "Retail & Mall", label: "Retail & Mall (RETAIL_MALL)" },
];

const AddSociety = () => {
  const navigate = useNavigate();

  const [societyName, setSocietyName] = useState("");
  const [societyType, setSocietyType] = useState("Residential");
  const [customPropertyType, setCustomPropertyType] = useState("");
  const [saveToMaster, setSaveToMaster] = useState(true);
  const [masterTypes, setMasterTypes] = useState([]);
  const [address, setAddress] = useState("");
  const [pincode, setPincode] = useState("");
  const [city, setCity] = useState("Mumbai");
  const [state, setState] = useState("Maharashtra");
  const [country] = useState("India");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    masterService
      .getPropertyTypes()
      .then((data) => {
        if (mounted && Array.isArray(data) && data.length > 0) {
          setMasterTypes(data.filter((t) => t.is_active));
        }
      })
      .catch((err) => {
        console.warn("Could not load dynamic property types, using defaults:", err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const propertyTypeOptions = useMemo(() => {
    const list =
      masterTypes.length > 0
        ? masterTypes.map((t) => ({
            value: t.name,
            label: `${t.name} (${t.code})`,
          }))
        : DEFAULT_PROPERTY_TYPES;

    return [
      ...list,
      { value: "__CUSTOM__", label: "✨ Other (Specify Custom Property Type...)" },
    ];
  }, [masterTypes]);

  const fullAddress = `${address}${city ? `, ${city}` : ""}${state ? `, ${state}` : ""}${pincode ? ` - ${pincode}` : ""}`;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!societyName.trim()) {
      toast.error("Please enter a property / society name.");
      return;
    }

    let effectiveType = societyType;
    if (societyType === "__CUSTOM__") {
      const trimmedCustom = customPropertyType.trim();
      if (!trimmedCustom) {
        toast.error("Please specify your custom property type name.");
        return;
      }
      effectiveType = trimmedCustom;
      if (saveToMaster) {
        try {
          await masterService.createPropertyType({
            name: trimmedCustom,
            description: `Custom property archetype added during ${societyName.trim()} onboarding.`,
          });
        } catch (err) {
          console.warn("Could not auto-save custom type to master taxonomy:", err);
        }
      }
    }

    try {
      setLoading(true);
      await createSociety({
        society_name: societyName.trim(),
        society_type: effectiveType,
        address: address.trim(),
        city: city.trim(),
        state: state.trim(),
        pincode: pincode.trim(),
        country,
        phone: phone.trim(),
        email: email.trim(),
        property_status: "operational",
      });
      toast.success(`Property "${societyName.trim()}" created successfully!`);
      navigate("/societies");
    } catch (err) {
      console.error("Error creating society:", err);
      toast.error(err.response?.data?.message || "Failed to create property.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Add New Property / Society">
      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "16px 8px 48px" }}>
        {/* Top Navigation */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/societies")}
            icon={<ArrowLeft size={16} />}
          >
            Back to Societies
          </Button>
          <span style={{ fontSize: "13px", color: "var(--color-text-muted)" }}>
            Properties / New Setup
          </span>
        </div>

        {/* Form Card */}
        <div
          style={{
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "16px",
            padding: "28px",
            boxShadow: "var(--shadow-xs)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                background: "rgba(37, 99, 235, 0.1)",
                color: "var(--color-primary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Building2 size={24} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 800, color: "var(--color-text-strong)" }}>
                Property Information
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: "13px", color: "var(--color-text-muted)" }}>
                Register a new residential or industrial complex on the AsmitA Platform.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
              <FormField label="Property / Society Name" required>
                <TextInput
                  placeholder="e.g. AsmitA Grand Mansion"
                  value={societyName}
                  onChange={(e) => setSocietyName(e.target.value)}
                  required
                />
              </FormField>

              <FormField
                label="Property Type"
                required
                action={
                  <Button
                    variant="ghost"
                    size="xs"
                    type="button"
                    onClick={() => navigate("/masters/property-types")}
                    style={{ fontSize: "11px", color: "var(--color-primary)", padding: "0 4px" }}
                  >
                    Manage Master Types
                  </Button>
                }
              >
                <SelectDropdown
                  value={societyType}
                  onChange={(e) => setSocietyType(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
                  options={propertyTypeOptions}
                  isSearchable={true}
                  isClearable={false}
                />
              </FormField>
            </div>

            {societyType === "__CUSTOM__" && (
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px dashed #cbd5e1",
                  borderRadius: "12px",
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <FormField
                  label="Specify Custom Property Type"
                  required
                  helperText="Enter the name for your custom property archetype (e.g. Resort, Data Center, Storage Hub)"
                >
                  <TextInput
                    placeholder="e.g. Resort & Hotel, Data Center, Warehousing Hub..."
                    value={customPropertyType}
                    onChange={(e) => setCustomPropertyType(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
                    required
                    autoFocus
                  />
                </FormField>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12.5px", color: "#475569", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={saveToMaster}
                    onChange={(e) => setSaveToMaster(e.target.checked)}
                    style={{ cursor: "pointer" }}
                  />
                  <span>Save this custom archetype to global Master Property Types taxonomy for future use</span>
                </label>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
              <FormField label="Contact Phone">
                <TextInput
                  type="tel"
                  placeholder="e.g. +91 9876543210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </FormField>

              <FormField label="Contact Email">
                <TextInput
                  type="email"
                  placeholder="e.g. operations@asmita.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FormField>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "16px" }}>
              <FormField label="Full Street Address" required>
                <TextInput
                  placeholder="Enter building number, street, landmark"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  required
                />
              </FormField>

              <FormField label="PinCode" required>
                <TextInput
                  placeholder="6 digit PIN"
                  value={pincode}
                  maxLength={6}
                  onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
                  required
                />
              </FormField>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
              <FormField label="City" required>
                <TextInput
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  required
                />
              </FormField>

              <FormField label="State" required>
                <TextInput
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  required
                />
              </FormField>

              <FormField label="Country">
                <TextInput value={country} disabled />
              </FormField>
            </div>

            {/* Google Map Preview */}
            {address && (
              <div style={{ marginTop: "8px" }}>
                <iframe
                  title="Google Map Preview"
                  width="100%"
                  height="220"
                  style={{ border: "1px solid var(--color-border)", borderRadius: "12px" }}
                  loading="lazy"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(fullAddress)}&output=embed`}
                />
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "16px", paddingTop: "16px", borderTop: "1px solid var(--color-border)" }}>
              <Button
                variant="outline"
                type="button"
                onClick={() => navigate("/societies")}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={loading}
                disabled={loading || !societyName.trim()}
              >
                Save Property
              </Button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AddSociety;